import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Language,
  LanguageCode,
  SUICustomerLanguageDropdown,
} from "../../../customers/customerlanguages";
import { FileDrop } from "../../../components/FileDrop";
import { useGetCustomerQuery } from "../../../api/customerApi";
import { NotificationAppearance } from "../../../api/djangoToastSlice";
import { updateTranslationMemoryEntries } from "../../../api/translationMemoryApi";
import { showMessage } from "../../../utils/partialUtils";
import { RootState } from "../../../utils/store";
import { Button } from "../../../components/tailwind/button/Button";
import { TranslationMemoryPaginationBar } from "./TranslationMemoryPaginationBar";
import { ToggleCheckbox } from "../../../components/tailwind";

type TranslationMemoryEntry = {
  id: number;
  source_text: string;
  target_text: string;
};

type TranslationMemoryFormProps = {
  customerLanguages: string;
  sourceLanguage: string;
  targetLanguage: string;
  formAction: string;
  useVectorStore?: string;
};

type ModifiedMap = Record<number, TranslationMemoryEntry>;
type NewEntryRow = {
  row_id: number;
  source_text: string;
  target_text: string;
};

export const TranslationMemoryForm: React.FC<TranslationMemoryFormProps> = ({
  customerLanguages,
  sourceLanguage,
  targetLanguage,
  formAction,
  useVectorStore: useVectorStoreProp = "0",
}) => {
  const { isLoading } = useGetCustomerQuery();
  const authToken = useSelector((state: RootState) => state.auth.token);
  const languages: Language[] = JSON.parse(customerLanguages);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState<
    Language
  >(JSON.parse(sourceLanguage));

  const [selectedTargetLanguage, setSelectedTargetLanguage] = useState<
    Language
  >(JSON.parse(targetLanguage));

  const [fetchedEntries, setFetchedEntries] = useState<
    TranslationMemoryEntry[]
  >([]);
  const [newEntryRows, setNewEntryRows] = useState<NewEntryRow[]>([
    { row_id: 0, source_text: "", target_text: "" },
  ]);
  const [nextNewRowId, setNextNewRowId] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [modifiedEntries, setModifiedEntries] = useState<ModifiedMap>({});
  const [isSaving, setIsSaving] = useState(false);

  const entryIndexById = useMemo((): Record<number, number> => {
    return fetchedEntries.reduce<Record<number, number>>(
      (acc, entry, index) => {
        acc[entry.id] = index;
        return acc;
      },
      {}
    );
  }, [fetchedEntries]);

  const totalPages = Math.max(1, Math.ceil(fetchedEntries.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const pageEnd = pageStart + pageSize;
  const pagedEntries = fetchedEntries.slice(pageStart, pageEnd);

  const downloadUrl = useMemo((): string => {
    const path = formAction.replace(/\/$/, "") + "/download.csv";
    const params = new URLSearchParams({
      source_lang: selectedSourceLanguage.iso,
      target_lang: selectedTargetLanguage.iso,
    });
    return `${path}?${params.toString()}`;
  }, [formAction, selectedSourceLanguage.iso, selectedTargetLanguage.iso]);
  const downloadDisabled = fetchedEntries.length === 0;

  const [vectorStoreEnabled, setVectorStoreEnabled] = useState<boolean>(
    useVectorStoreProp === "true" || useVectorStoreProp === "1"
  );

  const handleSourceLanguageChange = (iso: LanguageCode): void => {
    const language = languages.find((language) => language.iso === iso);
    setSelectedSourceLanguage(language);
  };

  const handleTargetLanguageChange = (iso: LanguageCode): void => {
    const language = languages.find((language) => language.iso === iso);
    setSelectedTargetLanguage(language);
  };

  const handleSourceTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    entryId: number
  ): void => {
    const newSourceText = e.target.value;
    const index = entryIndexById[entryId];
    if (index == null) {
      return;
    }

    setFetchedEntries((prev) => {
      const next = [...prev];
      const entry = next[index];
      next[index] = { ...entry, source_text: newSourceText };

      setModifiedEntries((m) => ({
        ...m,
        [entry.id]: next[index],
      }));

      return next;
    });
  };

  const handleTargetTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    entryId: number
  ): void => {
    const newTargetText = e.target.value;
    const index = entryIndexById[entryId];
    if (index == null) {
      return;
    }
    setFetchedEntries((prev) => {
      const next = [...prev];
      const entry = next[index];
      next[index] = { ...entry, target_text: newTargetText };

      setModifiedEntries((m) => ({
        ...m,
        [entry.id]: next[index],
      }));

      return next;
    });
  };

  const updateUrl = (nextSource: Language, nextTarget: Language): void => {
    const url = new URL(window.location.href);
    const currentSource = url.searchParams.get("source_lang");
    const currentTarget = url.searchParams.get("target_lang");

    const source = nextSource.iso;
    const target = nextTarget.iso;

    let changed = false;
    if (source && source !== currentSource) {
      url.searchParams.set("source_lang", source);
      changed = true;
    }
    if (target && target !== currentTarget) {
      url.searchParams.set("target_lang", target);
      changed = true;
    }

    if (changed) {
      window.history.replaceState({}, "", url.toString());
    }
  };

  const fetchEntries = async (
    sourceLang: string,
    targetLang: string
  ): Promise<void> => {
    try {
      const entriesPath = formAction.replace(/\/$/, "") + "/entries.json";
      const url = new URL(entriesPath, window.location.origin);
      url.searchParams.set("source_lang", sourceLang);
      url.searchParams.set("target_lang", targetLang);

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error("Failed to fetch entries");
      }
      const data = await response.json();
      setFetchedEntries(data.entries);
      setModifiedEntries({});
      setCurrentPage(1);
      setVectorStoreEnabled(data.use_vector_store ?? false);
    } catch (error) {
      console.error("Failed to fetch translation memory entries:", error);
    }
  };

  const handleSave = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    if (selectedFile) {
      return;
    }

    const hasManualRows = newEntryRows.some(
      (row) => row.source_text.trim() || row.target_text.trim()
    );

    if (hasManualRows) {
      return;
    }

    event.preventDefault();

    const updatedEntries = Object.values(modifiedEntries);
    if (updatedEntries.length === 0) {
      showMessage(
        NotificationAppearance.WARNING,
        "No changes to save in translation memory."
      );
      return;
    }
    if (!authToken) {
      showMessage(
        NotificationAppearance.ERROR,
        "Missing authentication token for translation memory save."
      );
      return;
    }

    setIsSaving(true);
    try {
      await updateTranslationMemoryEntries(
        {
          source_language: selectedSourceLanguage.iso,
          target_language: selectedTargetLanguage.iso,
          updated_entries: updatedEntries,
        },
        authToken
      );
      setModifiedEntries({});
      showMessage(
        NotificationAppearance.SUCCESS,
        "Translation memory changes saved."
      );
    } catch {
      showMessage(
        NotificationAppearance.ERROR,
        "Failed to save translation memory changes."
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (selectedFile && fileInputRef.current) {
      // Create a new FileList using DataTransfer API
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(selectedFile);
      fileInputRef.current.files = dataTransfer.files;
    } else if (!selectedFile && fileInputRef.current) {
      // Clear the input when file is removed
      fileInputRef.current.value = "";
    }
  }, [selectedFile]);

  useEffect(() => {
    updateUrl(selectedSourceLanguage, selectedTargetLanguage);
    fetchEntries(selectedSourceLanguage.iso, selectedTargetLanguage.iso);
  }, [selectedSourceLanguage, selectedTargetLanguage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageSizeChange = (size: number): void => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handlePaginationPrev = (): void => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handlePaginationNext = (): void => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  const handleNewRowSourceChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ): void => {
    const sourceText = e.target.value;
    setNewEntryRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], source_text: sourceText };
      return next;
    });
  };

  const handleNewRowTargetChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ): void => {
    const targetText = e.target.value;
    setNewEntryRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], target_text: targetText };
      return next;
    });
  };

  return (
    <form
      className="txu-form"
      method="post"
      onSubmit={handleSave}
      encType="multipart/form-data"
    >
      <input
        type="hidden"
        name="use_vector_store"
        value={vectorStoreEnabled ? "1" : "0"}
      />

      <header className="tw-flex tw-justify-between tw-items-center">
        <h1 className="tw-flex tw-items-center tw-gap-2 !tw-m-0 !tw-p-0 !tw-leading-none">
          <span>Translation Memory:</span>{" "}
          <span>
            {selectedSourceLanguage?.flag} {selectedSourceLanguage?.short_name}
          </span>{" "}
          <i className="material-icons tw-text-gray-500">arrow_forward</i>{" "}
          <span>
            {selectedTargetLanguage?.flag} {selectedTargetLanguage?.short_name}
          </span>
        </h1>
        <div className="tw-flex tw-items-center tw-gap-6">
          <div className="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer tw-select-none">
            <span
              className="tw-text-sm tw-font-medium tw-text-gray-700 tw-whitespace-nowrap"
              onClick={() => setVectorStoreEnabled((prev) => !prev)}
            >
              Use vector store for translation memory
            </span>
            <ToggleCheckbox
              checked={vectorStoreEnabled}
              setChecked={setVectorStoreEnabled}
            />
          </div>
          <button
            type="submit"
            className="txu-btn txu-primary tw-flex tw-items-center txu-icon"
            disabled={isSaving}
          >
            <i className="material-icons">save</i>Save
          </button>
        </div>
      </header>

      <details className="txu-accordion">
        <summary className="tw-flex tw-items-center">
          <i className="material-icons tw-text-gray-800 tw-mr-2">description</i>{" "}
          Import Translation Memory from file
        </summary>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-text-gray-500">
            Select a file to upload Translation Memory entries in batch. <br />
            File must be either CSV or Excel (.xlsx). Values are read from the
            first two columns and no header should be included, e.g.:
          </p>
          <table className="txu-table txu-compact txu-border tw-border-t tw-border-gray-200">
            <tbody>
              <tr>
                <td>dress</td>
                <td>Kleid</td>
              </tr>
              <tr>
                <td>baseball cap</td>
                <td>Baseballkappe</td>
              </tr>
              <tr>
                <td>mom-jeans</td>
                <td>mom-jeans</td>
              </tr>
              <tr>
                <td>⋮</td>
                <td>⋮</td>
              </tr>
            </tbody>
          </table>
          <div>
            <FileDrop
              selectedFile={selectedFile}
              isInvalid={false}
              onChange={(file): void => {
                setSelectedFile(file);
              }}
            />
            <input
              ref={fileInputRef}
              type="file"
              name="uploaded_file"
              style={{ display: "none" }}
            />
          </div>
          {fetchedEntries.length > 0 && (
            <label className=" tw-w-full !tw-flex tw-items-center tw-justify-center tw-gap-1">
              <input
                className="txu-input"
                type="checkbox"
                name="merge"
                value="1"
              />
              <span className="tw-uppercase tw-font-medium">
                Merge with existing items (below)
              </span>
            </label>
          )}
        </div>
      </details>

      <div className="txu-divider"></div>

      <article>
        <h3 className="tw-m-0">Translation Memory entries:</h3>
        <p className="tw-text-gray-500 tw-text-sm tw-m-0">
          Add or edit Translation Memory entries below
        </p>
        <div className="tw-grid tw-grid-cols-2 tw-gap-12 tw-my-4">
          <div className="tw-flex tw-items-center tw-gap-4">
            <label className="txu-label !tw-m-0" htmlFor="source_language">
              Source Language:
            </label>
            <SUICustomerLanguageDropdown
              loading={isLoading}
              className="!tw-w-1/2"
              fluid
              disabledLanguageOption={
                selectedTargetLanguage.iso as LanguageCode
              }
              placeholder="Select source language"
              selection
              value={selectedSourceLanguage.iso}
              onChange={(e, { value }): void =>
                handleSourceLanguageChange(value as LanguageCode)
              }
            />
          </div>
          <div className="tw-flex tw-items-center tw-gap-4">
            <label className="txu-label !tw-m-0" htmlFor="target_language">
              Target Language:
            </label>
            <SUICustomerLanguageDropdown
              loading={isLoading}
              className="!tw-w-1/2"
              fluid
              disabledLanguageOption={
                selectedSourceLanguage.iso as LanguageCode
              }
              placeholder="Select target language"
              selection
              value={selectedTargetLanguage.iso}
              onChange={(e, { value }): void =>
                handleTargetLanguageChange(value as LanguageCode)
              }
            />
            <a
              href={downloadDisabled ? undefined : downloadUrl}
              className={`txu-btn txu-secondary-alt tw-text-center ${
                downloadDisabled ? "tw-pointer-events-none tw-opacity-50" : ""
              }`}
              aria-disabled={downloadDisabled}
              data-testid="tm-download-csv"
            >
              Download as CSV
            </a>
          </div>
        </div>
        <div className="tw-my-4 tw-text-gray-500 tw-text-lg">
          Results: {fetchedEntries.length} entries found
        </div>
        <TranslationMemoryPaginationBar
          pageStart={pageStart}
          pageEnd={pageEnd}
          totalEntryCount={fetchedEntries.length}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          currentPage={currentPage}
          totalPages={totalPages}
          onGoPrev={handlePaginationPrev}
          onGoNext={handlePaginationNext}
          pageSizeSelectId="tm-page-size-top"
        />
        <div className="tw-mb-4">
          {newEntryRows.map((row, index) => (
            <div key={row.row_id} className="tw-grid tw-grid-cols-2 tw-gap-12">
              <input
                type="text"
                className="txu-input tw-my-1"
                placeholder="e.g. Hello!"
                name="sources"
                value={row.source_text}
                onChange={(e) => handleNewRowSourceChange(e, index)}
              />
              <input
                type="text"
                className="txu-input tw-my-1"
                placeholder="e.g. Guten Tag!"
                name="targets"
                value={row.target_text}
                onChange={(e) => handleNewRowTargetChange(e, index)}
              />
            </div>
          ))}
          <div className="tw-flex tw-justify-end tw-mt-2">
            <Button
              type="button"
              content="Add row"
              variant="primary-alt"
              onClick={(): void => {
                setNewEntryRows((prev) => [
                  ...prev,
                  {
                    row_id: nextNewRowId,
                    source_text: "",
                    target_text: "",
                  },
                ]);
                setNextNewRowId((prev) => prev + 1);
              }}
            />
          </div>
        </div>
        {pagedEntries.length > 0 &&
          pagedEntries.map((entry) => (
            <div key={entry.id} className="tw-grid tw-grid-cols-2 tw-gap-12">
              <input
                type="text"
                className="txu-input tw-my-1"
                placeholder="e.g. Hello!"
                name="sources"
                onChange={(e) => handleSourceTextChange(e, entry.id)}
                value={entry.source_text}
              />
              <input
                type="text"
                className="txu-input tw-my-1"
                placeholder="e.g. Guten Tag!"
                name="targets"
                onChange={(e) => handleTargetTextChange(e, entry.id)}
                value={entry.target_text}
              />
            </div>
          ))}
        <TranslationMemoryPaginationBar
          pageStart={pageStart}
          pageEnd={pageEnd}
          totalEntryCount={fetchedEntries.length}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          currentPage={currentPage}
          totalPages={totalPages}
          onGoPrev={handlePaginationPrev}
          onGoNext={handlePaginationNext}
          pageSizeSelectId="tm-page-size-bottom"
          className="tw-flex tw-items-center tw-justify-between tw-mt-3"
        />
      </article>
    </form>
  );
};
